import React from 'react';
import classes from './WeatherCard.module.css';
import { inferNoteEmoji } from './useWeatherData';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Purely presentational — all data-fetching lives in useWeatherData, shared
// with the collapsed weather chip in Home.js so the two don't each hit the
// forecast/climate APIs separately for the same festival.
const WeatherCard = ({ weatherData }) => {
  const {
    location, weatherNote,
    showOverride, showLive, showClimate,
    forecastDays, forecastLoading, forecastFailed,
    climateData, climateLoading, climateFailed,
    lat, lng, startdate,
  } = weatherData;

  // ── State 3 — admin override ──
  if (showOverride) {
    return (
      <div className={classes.overrideCard}>
        <span className={classes.overrideEmoji}>{inferNoteEmoji(weatherNote)}</span>
        <div className={classes.overrideBodyWrap}>
          <p className={classes.overrideHeading}>What to expect</p>
          <p className={classes.overrideBody}>{weatherNote}</p>
          <p className={classes.overrideFooter}>From the festival organisers</p>
        </div>
      </div>
    );
  }

  // ── State 2 — live forecast ──
  if (showLive) {
    if (forecastFailed) return null;
    if (forecastLoading || forecastDays === null) {
      return <div className={`${classes.card} ${classes.skeleton}`} />;
    }
    return (
      <div className={classes.card}>
        <div className={classes.headerRow}>
          <p className={classes.heading}>Festival days forecast</p>
          <span className={classes.liveBadge}>Live</span>
        </div>
        <div className={classes.tiles}>
          {forecastDays.map((d, i) => (
            <div className={classes.tile} key={i}>
              <p className={classes.tileLabel}>{DAYS[d.date.getDay()]} {d.date.getDate()}</p>
              <span className={classes.tileEmoji}>{d.emoji}</span>
              <p className={classes.tileTemp}>{d.temp}°</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── State 1 — climate average fallback ──
  if (!lat || !lng || !startdate) return null;
  if (climateFailed) return null;
  if (climateLoading || climateData === null) {
    return <div className={`${classes.card} ${classes.skeleton}`} />;
  }

  let hint = '';
  if (climateData.avgLow < 10) hint = 'Pack warm layers for cold nights';
  else if (climateData.avgLow < 15) hint = 'Pack layers for cool nights';
  else if (climateData.rainyDays > 3) hint = 'Pack a rain jacket';
  else if (climateData.avgHigh > 30) hint = 'Pack sunscreen and stay hydrated';

  const locationLabel = (location || '').split(',')[0];

  return (
    <div className={classes.card}>
      <p className={classes.heading}>
        Expect in {climateData.month}
        {locationLabel && <span className={classes.headingLocation}> · {locationLabel}</span>}
      </p>
      <div className={classes.tiles}>
        <div className={classes.tile}>
          <span className={classes.tileEmoji}>☀️</span>
          <p className={classes.tileTemp}>{climateData.avgHigh}°</p>
          <p className={classes.tileLabel}>Avg high</p>
        </div>
        <div className={classes.tile}>
          <span className={classes.tileEmoji}>🌙</span>
          <p className={classes.tileTemp}>{climateData.avgLow}°</p>
          <p className={classes.tileLabel}>Avg low</p>
        </div>
        <div className={classes.tile}>
          <span className={classes.tileEmoji}>🌧️</span>
          <p className={classes.tileTemp}>{climateData.rainyDays}</p>
          <p className={classes.tileLabel}>Rainy days</p>
        </div>
      </div>
      <p className={classes.footerRow}>
        <span className={classes.footer}>Based on historical averages</span>
        {hint && <span className={classes.hint}> · {hint}</span>}
      </p>
    </div>
  );
};

export default WeatherCard;
